/**
 * Meta Graph API rate-limit (quota) classification — pure, no I/O, no React.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Meta app's Graph quota is `200 x effective_users_count` calls per hour.
 * For a 17-user org that is ~4,080 calls/HOUR for the WHOLE workspace, and it
 * cannot be raised. Every endpoint under `/ads/*` that proxies Meta live spends
 * from that one shared bucket, so exhaustion is an ORG-WIDE outage, not a
 * per-user hiccup.
 *
 * Two rules follow, and both are encoded here rather than re-derived per call
 * site:
 *
 *  1. NEVER auto-retry a rate-limit failure. A retry is another call against an
 *     already-empty bucket: it cannot succeed and it lengthens the outage for
 *     everyone else in the org. `shouldRetryMetaQuery` refuses every 4xx (429
 *     included) and only ever retries a transient 5xx/network blip.
 *
 *  2. NEVER let a rate-limited fetch render as an empty/zero state. "We could
 *     not find out" is not "there is nothing" — that class of lie has already
 *     shipped here once (a failed ad-account list rendered as "every account is
 *     already connected"). Call sites use `isMetaRateLimitError` to branch into
 *     a distinguishable degraded state.
 *
 * BACKEND CONTRACT
 * ----------------
 * A quota failure comes back as HTTP **429** with a `Retry-After` header (whole
 * seconds) and a body carrying the stable code `META_RATE_LIMITED` plus a
 * retry-after value. `apiClient` throws an `ApiError` for any non-2xx, so that
 * is the shape reaching these helpers. Both the header (lifted onto
 * `ApiError.retryAfter` by the client) and every plausible body location are
 * read, because the header is the only source apiClient could otherwise not
 * preserve.
 *
 * LEGACY SHAPE, DELIBERATELY STILL MATCHED
 * ----------------------------------------
 * Before the 429 contract landed, the same failure surfaced as a **500** with
 * the raw Meta sentence "(#4) Application request limit reached". Deploys are
 * not atomic (the web app can ship ahead of the backend, and vice versa), so
 * the raw-message match stays as a fallback. Do NOT remove it while any
 * environment can still be running the old backend — without it a live outage
 * silently degrades back to a generic "something went wrong".
 */

/** The stable machine code the backend sends on a Meta quota failure. */
export const META_RATE_LIMITED_CODE = "META_RATE_LIMITED";

/**
 * Meta's own numeric error codes for "you are out of quota".
 *   4     — Application request limit reached (the app-level bucket; ours)
 *   17    — User request limit reached
 *   32    — Page-level request limit reached
 *   613   — Calls to this API have exceeded the rate limit
 *   80004 — Ads Management rate limit
 *   80007 — Marketing API rate limit
 */
const META_THROTTLE_CODES = new Set([4, 17, 32, 613, 80004, 80007]);

/**
 * Raw-sentence fallback for the pre-429 backend. Deliberately narrow: it must
 * match Meta's throttle wording and nothing else, so an unrelated failure is
 * never mislabelled as a quota outage (which would tell the user to "wait a few
 * minutes" for a problem that waiting cannot fix).
 */
const RAW_THROTTLE_TEXT =
  /application request limit reached|user request limit reached|calls to this api have exceeded|request limit reached|\(#4\)|\(#17\)|\(#613\)|\(#80004\)|\(#80007\)/i;

/** The loosely-shaped things a call site can hand us. */
export interface MetaErrorLike {
  statusCode?: unknown;
  status?: unknown;
  code?: unknown;
  errorCode?: unknown;
  message?: unknown;
  error?: unknown;
  retryAfter?: unknown;
  retry_after?: unknown;
  meta?: unknown;
  details?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

/** Every string on the error worth pattern-matching, flattened into one blob. */
function textOf(err: unknown): string {
  if (typeof err === "string") return err;
  const rec = asRecord(err);
  if (!rec) return "";
  const parts: unknown[] = [rec.message, rec.error];
  const nested = asRecord(rec.error);
  if (nested) parts.push(nested.message, nested.error);
  const details = asRecord(rec.details);
  if (details) parts.push(details.message, details.error);
  return parts.filter((p) => typeof p === "string").join(" ");
}

/** Numeric HTTP status, from either field name. ApiError uses `statusCode`. */
export function statusOf(err: unknown): number | null {
  const rec = asRecord(err);
  if (!rec) return null;
  const raw = rec.statusCode ?? rec.status;
  const n = typeof raw === "string" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/** Machine codes present on the error, lowercased for comparison. */
function machineCodes(err: unknown): string[] {
  const rec = asRecord(err);
  if (!rec) return [];
  const nested = asRecord(rec.error);
  const raw = [rec.code, rec.errorCode, nested?.code];
  return raw
    .filter((c) => typeof c === "string" || typeof c === "number")
    .map((c) => String(c).trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Is this failure the Meta quota being exhausted?
 *
 * True for: the new contract (429 / `META_RATE_LIMITED`), a Meta throttle code
 * carried in `code`, and the legacy raw-sentence 500. False for everything else
 * — including a plain 500, which must keep reading as a generic failure.
 */
export function isMetaRateLimitError(err: unknown): boolean {
  if (!err) return false;

  if (statusOf(err) === 429) return true;

  for (const code of machineCodes(err)) {
    if (code === META_RATE_LIMITED_CODE.toLowerCase()) return true;
    if (code === "rate_limited" || code === "rate_limit_exceeded") return true;
    const n = Number(code);
    if (Number.isFinite(n) && META_THROTTLE_CODES.has(n)) return true;
  }

  return RAW_THROTTLE_TEXT.test(textOf(err));
}

/** Meta's bucket is hourly, so anything longer than this is noise. */
const ONE_HOUR_SECONDS = 3600;

/**
 * Seconds until the caller may try again, or `null` when the backend did not
 * say.
 *
 * `null` is a real answer and must stay distinguishable from `0`: "we do not
 * know when it resets" is not "try again immediately". Values are clamped to
 * (0, 1 hour] — a larger number is noise, and a non-positive one carries no
 * information.
 */
export function retryAfterSecondsFrom(err: unknown): number | null {
  const rec = asRecord(err);
  if (!rec) return null;

  const meta = asRecord(rec.meta);
  const details = asRecord(rec.details);
  const nested = asRecord(rec.error);
  const nestedMeta = asRecord(nested?.meta);

  const candidates: unknown[] = [
    rec.retryAfter,
    rec.retry_after,
    meta?.retryAfter,
    meta?.retry_after,
    meta?.retry_after_seconds,
    details?.retryAfter,
    details?.retry_after,
    nested?.retryAfter,
    nested?.retry_after,
    nestedMeta?.retryAfter,
    nestedMeta?.retry_after,
  ];

  for (const raw of candidates) {
    // A boolean/array/object must never coerce into a duration: Number(true) is
    // 1 and Number([]) is 0, both of which would invent a reset time.
    if (typeof raw !== "number" && typeof raw !== "string") continue;
    if (typeof raw === "string" && raw.trim() === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) continue;
    return Math.min(Math.ceil(n), ONE_HOUR_SECONDS);
  }

  return null;
}

/**
 * Human phrasing for a wait, or `null` when the duration is unknown so the
 * caller can omit the sentence entirely rather than print "in null minutes".
 */
export function formatRetryAfter(seconds: number | null | undefined): string | null {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return "less than a minute";
  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) return "about a minute";
  if (minutes < 60) return `about ${minutes} minutes`;
  return "about an hour";
}

export interface MetaRateLimitNotice {
  title: string;
  /** One sentence: what happened, and what the reader should do. */
  description: string;
  /** Seconds until reset, or null when the backend did not say. */
  retryAfterSeconds: number | null;
}

/**
 * The copy shown wherever a Meta quota failure surfaces.
 *
 * Deliberately says WORKSPACE, not "you": the bucket is shared org-wide, so the
 * person reading this may have spent none of it, and "you made too many
 * requests" would be false. It also states that the reset is automatic — the
 * old raw string "(#4) Application request limit reached" gave the user nothing
 * to do and read like a permanent breakage.
 */
export function metaRateLimitNotice(err: unknown): MetaRateLimitNotice {
  const retryAfterSeconds = retryAfterSecondsFrom(err);
  const wait = formatRetryAfter(retryAfterSeconds);
  return {
    title: "Facebook data is temporarily unavailable",
    description: wait
      ? `This workspace has reached Meta's hourly limit for Facebook API requests. It resets automatically — try again in ${wait}.`
      : "This workspace has reached Meta's hourly limit for Facebook API requests. It resets automatically within the hour — please try again shortly.",
    retryAfterSeconds,
  };
}

/**
 * Retry predicate for every React Query that reaches a Meta-proxying endpoint.
 *
 * - 4xx (429 included) → NEVER. A 429 retry is a call against an empty bucket;
 *   any other 4xx is a settled answer ("no ad account connected") that a repeat
 *   cannot change.
 * - 5xx / network → up to `maxRetries`, so an ordinary blip still self-heals.
 *
 * `maxRetries` exists so this can replace a hand-rolled predicate WITHOUT
 * changing that query's behaviour on transient failures: several ads hooks
 * already allowed 2 retries and only skipped 400/401/403, which meant a 429 was
 * retried twice — three calls against an exhausted bucket. Pass their existing
 * ceiling and the only behaviour that changes is the rate-limit branch.
 */
export function shouldRetryMetaQuery(
  failureCount: number,
  err: unknown,
  maxRetries = 1
): boolean {
  if (isMetaRateLimitError(err)) return false;
  const status = statusOf(err);
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < maxRetries;
}
